import cn from 'classnames'
import { Formik } from 'formik'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import React, { MutableRefObject, useEffect, useMemo, useState } from 'react'

import { ConditionalWrapper } from '@/components/ConditionalWrapper'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import api, { ContactAgencyPayload, ContactCasavoPayload, FavouriteSearchOrigin } from '@/lib/api-gluglu-open'
import { ListingCate } from '@/page-commons/listingBySlug'
import { flexCenter, gap } from '@/shared-styles/styles.css'
import { anchorSmoothScroll } from '@/utils/dom-interactions-helpers'
import { getGAClientId } from '@/utils/get-ga-client-id'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { isThirdParty } from '@/utils/listing/listingType'
import { langToCountryCode, getPrefixFromCountryCode } from '@/utils/prefix'
import { isValidUserFor } from '@/utils/third-party/recaptcha/api'
import { handleFormSubmit, handleSystem } from '@/utils/tracking/eventsHandlers'

import { RequestContactCTA } from '../RequestContactCTA'

import FormContentAgency from './FormContentAgency'
import FormContentCasavo from './FormContentCasavo'
import FormHeaderDesktop from './FormHeaderDesktop/FormHeaderDesktop'
import FormHeaderMobile from './FormHeaderMobile'
import Success from './Success'
import { FormFields } from './types'
import { getValidationSchema } from './validationSchema'

let labourIllusionTimeTimeout

type Props = {
  cities: ContextValue
  className?: string
  onClose?: VoidFunction
  onSuccess?: (success: boolean) => void
  property: ListingCate
  similarPropertiesLength: number
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}

export const ContactAgencyForm: React.FC<Props> = ({
  cities,
  className,
  onClose,
  onSuccess,
  property,
  similarPropertiesLength,
  similarPropertiesRef,
}) => {
  const { lang, t } = useTranslation()

  const [isInSuccess, setIsInSuccess] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [sendingMail, setSendingMail] = useState(false)
  const [loading, setLoading] = useState(false)
  const headerHeightDesktop = 100
  const [emailValue, setEmailValue] = useState('')

  const listingTitle = property.detail.titles.find((title) => title.language === lang).text
  const thirdParty = isThirdParty(property)

  useEffect(() => {
    return () => clearTimeout(labourIllusionTimeTimeout)
  }, [])

  useEffect(() => {
    if (isStorageSupported(() => sessionStorage) && sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')) {
      const saveSearchAfterLoginParams = JSON.parse(
        sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      ) as {
        origin: FavouriteSearchOrigin
        pageUrl: string
      }

      if (
        saveSearchAfterLoginParams.origin === 'LISTING_CONTACT' &&
        saveSearchAfterLoginParams.pageUrl === window.location.href
      ) {
        setIsInSuccess(true)
        return
      } else {
        sessionStorage.removeItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      }
    }
    setIsInSuccess(false)
  }, [property.slug])

  useEffect(() => {
    onSuccess && onSuccess(isInSuccess)
  }, [onSuccess, isInSuccess])

  const device = useDevice()

  const Header = () =>
    device === 'mobile' ? <FormHeaderMobile property={property} /> : <FormHeaderDesktop property={property} />

  const handleSubmitContactForm = async (values: FormFields) => {
    setSendingMail(true)
    setLoading(true)
    setHasError(false)
    setEmailValue(values.leadEmail)

    try {
      const startApiCall = new Date().getTime()

      await isValidUserFor('contactAgency')

      if (property.contactInfo.ownedByCasavo) {
        const payload: ContactCasavoPayload = {
          consentForCasavoMortgageService: values.consentForCasavoMortgageService,
          currentLanguage: lang,
          email: values.leadEmail,
          keepMeUpdated: values.hasAcceptedMarketing,
          name: values.leadName,
          phoneNumber: values.leadPhoneNumberPrefix + values.leadPhoneNumber.replace(/-/g, ''),
          reason: values.leadReason,
          trackingClientId: getGAClientId(),
        }
        await api.listingInfo(property.id, payload)
      } else {
        const payload: ContactAgencyPayload = {
          currentLanguage: lang,
          lead: {
            consentForCasavoMortgageService: values.consentForCasavoMortgageService,
            email: values.leadEmail,
            hasAcceptedMarketing: values.hasAcceptedMarketing,
            message: values.leadMessage,
            phoneNumber: values.leadPhoneNumberPrefix + values.leadPhoneNumber.replace(/-/g, ''),
          },
          listingId: property.id,
          trackingClientId: getGAClientId(),
        }
        await api.contactAgency(payload)
      }
      handleFormSubmit('ListingDetailForm', 'ContactAgency')
      const diffFromStart = new Date().getTime() - startApiCall
      const labourIllusionTime = Math.max(1000 - diffFromStart, 0)
      labourIllusionTimeTimeout = setTimeout(() => {
        setIsInSuccess(true)
        setSendingMail(false)
        setLoading(false)
        device === 'desktop' && anchorSmoothScroll('#contactForm', -headerHeightDesktop)
        device === 'mobile' &&
          property.detail.casavoDirectSale &&
          anchorSmoothScroll('#contactForm', parseInt(theme.spacings['-2xl']))
      }, labourIllusionTime)
    } catch (e) {
      setHasError(true)
      setSendingMail(false)
      setLoading(false)
      handleSystem('ListingDetailForm', 'ContactAgencyError')
    }
  }

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  const validationSchema = getValidationSchema(t, property.contactInfo.ownedByCasavo ? 'casavo' : 'agency')
  const defaultLeadPhoneNumberPrefix = getPrefixFromCountryCode(langToCountryCode(lang))
  const initialValues = property.contactInfo.ownedByCasavo
    ? {
        consentForCasavoMortgageService: false,
        hasAcceptedMarketing: false,
        leadEmail: '',
        leadMessage: '',
        leadName: '',
        leadPhoneNumber: '',
        leadPhoneNumberPrefix: defaultLeadPhoneNumberPrefix,
        leadReason: '',
      }
    : {
        consentForCasavoMortgageService: false,
        hasAcceptedMarketing: false,
        leadEmail: '',
        leadMessage: '',
        leadPhoneNumber: '',
        leadPhoneNumberPrefix: defaultLeadPhoneNumberPrefix,
      }

  const FormContent = useMemo(
    () => () =>
      (
        <>
          {isThirdParty ? (
            <FormContentAgency hasError={hasError} loading={loading} sendingMail={sendingMail} />
          ) : (
            <FormContentCasavo
              hasError={hasError}
              loading={loading}
              phoneNumber={property.contactInfo.telephoneNumberContact}
              property={property}
              sendingMail={sendingMail}
            />
          )}
        </>
      ),
    [property.contactInfo.ownedByCasavo, property.contactInfo.telephoneNumberContact, hasError, loading, sendingMail]
  )

  return (
    <ConditionalWrapper
      condition={device === 'desktop'}
      wrapper={(children) => (
        <div
          className={sprinkles({
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
          })}
        >
          <div className={sprinkles({ flexShrink: 2, marginTop: '2xl' })}>
            <img
              alt=""
              className={sprinkles({ width: '100%' })}
              loading="lazy"
              src={`${imageBasePath}/images/concepts/clouds-left${device === 'mobile' ? '-small' : ''}.svg`}
            />
          </div>
          {children}
          <div className={sprinkles({ flexShrink: 2, marginTop: '2xl' })}>
            <img
              alt=""
              className={sprinkles({ width: '100%' })}
              loading="lazy"
              src={`${imageBasePath}/images/concepts/clouds-right${device === 'mobile' ? '-small' : ''}.svg`}
            />
          </div>
        </div>
      )}
    >
      {!isInSuccess ? (
        <div className={className} id="contactForm">
          <Header />

          {thirdParty ? (
            <Formik
              validateOnBlur
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmitContactForm}
            >
              <FormContent />
            </Formik>
          ) : (
            <div className={cn(flexCenter, gap.xl, sprinkles({ paddingX: { desktop: '5xl' } }))}>
              <RequestContactCTA
                listing={property}
                listingTitle={listingTitle}
                requestType={'info'}
                similarPropertiesRef={similarPropertiesRef}
              />

              <RequestContactCTA
                listing={property}
                listingTitle={listingTitle}
                requestType="visit"
                similarPropertiesRef={similarPropertiesRef}
              />
            </div>
          )}
        </div>
      ) : (
        <Success
          cities={cities}
          listing={property}
          similarPropertiesLength={similarPropertiesLength}
          userEmail={emailValue}
          onGoToSimilarProperties={onClose}
        />
      )}
    </ConditionalWrapper>
  )
}
